
import { type Context } from "@nuxt/types";
import {
  ComponentOptions,
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  useMeta,
  useRoute,
  useStore,
  watch,
} from "@nuxtjs/composition-api";
import Vue from "vue";

import LtkCoachmark from "@/components/Ltk/LtkCoachmark.vue";
import {
  useAmplitude,
  useBranchService,
  useConfig,
  useLinkService,
  useMetaService,
  useTranslate,
  useVuetify,
} from "@/composables";
import { getAmplitudeStore } from "@/store/amplitude";
import { useCreatorShop } from "@/store/creator-shop";
import { getExperimentsStore } from "@/store/experiments";
import { getLtksStore } from "@/store/ltks";
import { getMediaObjectsStore } from "@/store/media-objects";
import { getProductDetailsStore } from "@/store/product-details";
import { getProductsStore } from "@/store/products";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode } from "@/utils/api";
import { LTK_COACHMARK_CLASS_NAME } from "@/utils/constants";
import { encode as asciiEncode } from "@/utils/encoding/ascii";
import { encode as htmlEncode } from "@/utils/encoding/html";
import { FOUND, NOT_FOUND } from "@/utils/enums/http-codes";
import { time } from "@/utils/enums/time";
import {
  EXPERIMENTS_POSTS_FAVORITE_VPM,
  EXPERIMENTS_PRODUCTS_ABOVE_FOLD_ON_POST_DETAILS,
  EXPERIMENTS_SINGLE_COLUMN,
} from "@/utils/experiments/constants";
import { COACHMARK_POST_PAGE } from "@/utils/localstorage/constants";
import logger, { logLevel } from "@/utils/logger";
import { TOOLTIP_REFERRER_POST_PAGE_LIKE_TIMESTAMP } from "@/utils/sessionstorage/constants";

export default defineComponent({
  name: "LtkPostPage",
  setup() {
    const store = useStore();
    const route = useRoute();
    const amplitude = useAmplitude();
    const linkService = useLinkService();
    const metaService = useMetaService();
    const vuetify = useVuetify();
    const branch = useBranchService();
    const { t } = useTranslate();
    const config = useConfig();

    const profilesStore = getProfilesStore(store);
    const creatorShop = useCreatorShop();
    const experimentsStore = getExperimentsStore(store);
    const ltksStore = getLtksStore(store);
    const mediaObjectsStore = getMediaObjectsStore(store);

    const id = ref(route.value.params.id);
    const displayName = ref(route.value.params.displayName);
    const productIdParam = ref(route.value?.query?.product ?? null);
    const scrollY = ref(0);
    const productsPosition = ref(0);
    const canShowCoachmark = ref(false);
    const intersectedItems = ref<Array<IntersectionObserverEntry>>([]);

    onMounted(() => {
      setTimeout(async () => {
        if (isEligibleForCoachmark.value) {
          if (await isInTooltipExperimentGroup()) {
            canShowCoachmark.value = true;
          }
        }
      }, time.TWO_SECONDS);

      const aspectRatio = (ltk.value?.heroImageWidth ?? 1) / (ltk.value?.heroImageHeight ?? 1);
      const aspectRationPercision = 3;
      const productId = Array.isArray(productIdParam.value) ? productIdParam.value[0] : productIdParam.value ?? "";

      creatorShop.setReferringShopTab();
      amplitude?.sendEvent("/page/single-ltk", {
        "aspect-ratio": parseFloat(aspectRatio.toFixed(aspectRationPercision)),
        "display-name": profile.value?.displayName ?? "",
        "has-video": Boolean(ltk.value?.videoMediaId),
        "ltk-id": id.value,
        "product-count": (ltk.value?.productIds ?? []).length,
        published: Boolean(
          !(
            ltk.value?.status === "DRAFT" ||
            ltk.value?.status === "DRAFT_SCHEDULED" ||
            ltk.value?.status === "PUBLISH_AFTER_CONTENT_VALIDATED"
          ),
        ),
        "publisher-id": ltk.value?.profileId ?? "",
        "has-title": Boolean(ltk.value?.title),
        "product-id": productId ?? "",
      });
    });

    onBeforeUnmount(() => {
      creatorShop.completeShopTabJourney();
    });

    const ltk = computed(() => {
      return ltksStore.ltks[id.value];
    });

    const profile = computed(() => {
      if (ltk.value === undefined) {
        return undefined;
      }

      return profilesStore.profiles[ltk.value.profileId];
    });

    const profileId = computed(() => {
      return profile.value?.id || "";
    });

    const heroImageSrcSet = computed(() => {
      const src = linkService.getImageLink({
        url: ltk.value?.heroImage ?? "",
        width: "450",
        crop: false,
        useDefaultDpr: false,
      });
      return `${src}&dpr=1 1x, ${src}&dpr=2 2x, ${src}&dpr=3 3x`;
    });

    const productName = computed(() => {
      const maxProductNameLength = 35;
      const productsStore = getProductsStore(store);
      const productDetailsStore = getProductDetailsStore(store);

      /*
       If the route has a product id param then we want to return the product name of that product
      */
      if (productIdParam.value && typeof productIdParam.value === "string") {
        const productDetailId = productsStore.products[productIdParam.value]?.productDetailsId;
        if (productDetailId) {
          return metaService.truncateString(
            productDetailsStore.productDetails[productDetailId]?.name ?? "product",
            maxProductNameLength,
          );
        }
      }

      const detail = (ltk.value?.productIds ?? [])
        .map((id) => {
          const productDetailId = productsStore.products[id]?.productDetailsId;
          if (productDetailId) {
            return productDetailsStore.productDetails[productDetailId];
          }

          return undefined;
        })
        .find((detail) => detail !== undefined);
      return metaService.truncateString(detail?.name ?? "product", maxProductNameLength);
    });

    const topLeftMasonTile = computed(() => {
      const MAX_Y_POS = 100;
      let lowest = intersectedItems.value[0];

      for (const element of intersectedItems.value) {
        // get elements current boundary
        const lowestRect = lowest.target.getBoundingClientRect();
        const elementRect = element.target.getBoundingClientRect();

        if (elementRect.y < lowestRect.y || lowestRect.y < MAX_Y_POS) {
          lowest = element;
        }
      }

      return lowest;
    });

    const isEligibleForCoachmark = computed(() => {
      return vuetify.breakpoint.xsOnly && !hasAlreadySeenCoachmark.value && !branch.isJourneyDisplayed();
    });

    const hasAlreadySeenCoachmark = computed(() => {
      // ensure that there is not already a coachmark displayed
      const coachmarkElementArray = document.getElementsByClassName(LTK_COACHMARK_CLASS_NAME);
      return localStorage.getItem(COACHMARK_POST_PAGE) === "true" || coachmarkElementArray.length > 0;
    });

    const onScroll = () => {
      scrollY.value = window.pageYOffset;
    };

    const onProductPosition = (position: number) => {
      productsPosition.value = position;
    };

    const onIntersectObserved = (entries: Array<IntersectionObserverEntry>) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        const MAX_X_POS = 15;
        const isEntryOnTheLeft = entry.boundingClientRect.x < MAX_X_POS;

        if (entry.isIntersecting && isEntryOnTheLeft) {
          intersectedItems.value.push(entry);
        }
      });
    };

    const profileClick = () => {
      getAmplitudeStore(store).setReferringComponent(
        amplitude?.getReferringComponentByPage(route.value.name ?? "", "related posts section") ?? "",
      );
    };

    const singleColumnExperimentName = computed(() => EXPERIMENTS_SINGLE_COLUMN);

    const isInTooltipExperimentGroup = (): boolean => {
      return Boolean(experimentsStore.variant(EXPERIMENTS_POSTS_FAVORITE_VPM)?.value);
    };

    watch(
      () => intersectedItems.value,
      (value: Array<IntersectionObserverEntry>) => {
        const MAX_NUM_OF_VISIBLE_ELEMENTS = 6;

        if (value.length > MAX_NUM_OF_VISIBLE_ELEMENTS) {
          intersectedItems.value = intersectedItems.value.slice(-MAX_NUM_OF_VISIBLE_ELEMENTS);
        }

        if (!hasAlreadySeenCoachmark.value && canShowCoachmark.value) {
          const coachmarkMount = topLeftMasonTile.value.target.querySelector("#coachmarkMount");
          const likeButton = topLeftMasonTile.value.target.querySelector("#coachmarkTarget");
          const coachmarkText = t("Tap the heart to save your picks for later and be notified before they sell out.");

          // mount coachmark component, and push like button through overlay
          const Coachmark = Vue.extend(LtkCoachmark as ComponentOptions<Vue>);
          new Coachmark({
            propsData: {
              text: coachmarkText,
              bottom: "60px",
              tailBottom: "-12px",
              tailLeft: "36vw",
              tailPointDown: true,
              displayedInViewportDelay: time.HALF_SECOND,
              handleIsDisplayedInViewport: () => {
                try {
                  localStorage.setItem(COACHMARK_POST_PAGE, "true");
                  // set value to enabled attribution to tooltip referrer for the session
                  sessionStorage.setItem(TOOLTIP_REFERRER_POST_PAGE_LIKE_TIMESTAMP, `${Date.now()}`);
                } catch (e) {
                  console.error("There is an issue accessing local and/or session storage: ", e);
                }

                amplitude?.sendEvent("/web-vpm-displayed", {
                  "vpm-type": "favorite a post",
                });
              },
            },
          }).$mount(coachmarkMount as HTMLElement);

          likeButton?.setAttribute(
            "style",
            `z-index: ${window.getComputedStyle(document.body).getPropertyValue("--z-like-and-follow-button")}`,
          );
          canShowCoachmark.value = false;
        }
      },
    );

    useMeta(() => {
      const branchMeta = {
        title: t("Get my full shop experience").value,
        title_treatment: t("Get my full LTK experience").value,
        subtitle: t("Download the LTK app now to shop all of my favorite finds in one place.").value,
        subtitle_treatment: t("Download the LTK app to shop my favorites and get notified when I post fresh content.")
          .value,
        avatarUrl: profile.value?.avatarUrl,
        button: t("Get the free LTK app").value,
        button_treatment: t("Get the LTK app").value,
        getButton: t("GET").value,
        openButton: t("OPEN").value,
        continue: t("Continue on web").value,
        page: "post_page",
      };
      const _displayName = asciiEncode(displayName.value ?? "");
      const bio = profile.value?.bio
        ? htmlEncode(profile.value?.bio)
        : "Shop all of my favorites right from my LTK shop!";
      const caption = ltk.value?.caption ? htmlEncode(ltk.value?.caption) : "";
      const creatorUrl = new URL(`https://${config.ltkSite}/`);
      creatorUrl.pathname = `/explore/${_displayName}`;
      const blogUrl = new URL(`https://${config.ltkSite}/`);
      blogUrl.pathname = `/explore/${_displayName}/posts/${id.value}`;

      const jsonld = {
        "@context": "http://schema.org/",
        "@type": "BlogPosting",
        sharedContent: {
          "@type": "CreativeWork",
          thumbnailUrl: ltk.value?.heroImage,
          text: caption,
        },
        articleBody: caption,
        creator: {
          "@type": "Organization",
          description: bio,
          logo: profile.value?.avatarUrl,
          name: `${_displayName} | LTK`,
          url: creatorUrl.href,
        },
        datePublished: ltk.value?.datePublished,
        headline: `${productName.value} Curated on LTK`,
        image: ltk.value?.heroImage,
        url: blogUrl.href,
      };

      if (!ltk) {
        logger(logLevel.ERROR, "Rendering head without ltk model", { hash: id.value });
        return {};
      }

      if (
        ltk.value?.status === "DRAFT" ||
        ltk.value?.status === "DRAFT_SCHEDULED" ||
        ltk.value?.status === "PUBLISH_AFTER_CONTENT_VALIDATED"
      ) {
        const title = t("Coming Soon...");

        return metaService.setPageMeta({ title, customTitle: true, noIndex: true, branchMeta });
      } else if (ltk.value?.status === null) {
        const title = t("Post Deleted");

        return metaService.setPageMeta({ title, customTitle: true, noIndex: true, branchMeta });
      }

      const productTranslationParams = { productName: productName.value };

      const title = t("{productName} curated on LTK", productTranslationParams);

      const description = t(
        "Shop {productName} and other curated products on LTK, the easiest way to shop everything from your favorite creators.",
        productTranslationParams,
      );

      const head = metaService.setPageMeta({
        title: title.value,
        customTitle: true,
        description: description.value,
        branchMeta,
      });

      const image = ltk.value?.heroImage ?? "";
      const sharingImage = computed(() => {
        // If the route has a product id param, we want to return the product image instead of the ltk hero image to avoid confusion
        if (productIdParam.value && typeof productIdParam.value === "string") {
          const productsStore = getProductsStore(store);
          const productDetailsStore = getProductDetailsStore(store);
          const productDetailId = productsStore.products[productIdParam.value]?.productDetailsId;

          if (productDetailId) {
            return productDetailsStore.productDetails[productDetailId]?.imageUrl ?? image;
          }
        }

        return image;
      });
      const defaultWidth = 450;
      const heroImageWidth = ltk.value?.heroImageWidth ?? defaultWidth;
      const heroImageHeight = ltk.value?.heroImageHeight ?? heroImageWidth;

      head.meta?.push(...metaService.setOGImg(sharingImage.value, heroImageWidth, heroImageHeight));

      const mediaObject = mediaObjectsStore.mediaObjects[ltk.value?.videoMediaId ?? ""];
      // If sharing a specific product from the post, to avoid confusion do not add the post's video
      if (mediaObject && !productIdParam.value) {
        const video = mediaObject.brandedMediaCdnUrl;
        const videoHeight = mediaObject.typeProperties.height;
        const videoWidth = mediaObject.typeProperties.width;

        head.meta?.push(
          metaService.setMetaProp("twitter:player", video),
          metaService.setMetaProp("twitter:player:height", `${videoHeight}`),
          metaService.setMetaProp("twitter:player:width", `${videoWidth}`),
          metaService.setMetaProp("og:video", video),
          metaService.setMetaProp("og:video:secure_url", video),
          metaService.setMetaProp("og:video:type", "video/mp4"),
          metaService.setMetaProp("og:video:height", `${videoHeight}`),
          metaService.setMetaProp("og:video:width", `${videoWidth}`),
          metaService.setMetaName("twitter:card", "player"),
        );
      }

      head.link?.push({
        vmid: "preload",
        rel: "preload",
        as: "image",
        imagesrcset: heroImageSrcSet.value,
      });
      head.__dangerouslyDisableSanitizersByTagID = {
        ...head.__dangerouslyDisableSanitizersByTagID,
        // query param & in the image urls are being converted into HTML entities and making this property invalid
        preload: ["imagesrcset"],
      };

      head.script = [
        {
          type: "application/ld+json",
          json: JSON.parse(JSON.stringify(jsonld)),
        },
      ];

      return head;
    });
    return {
      ltk,
      productIdParam,
      profile,
      profileId,
      heroImageSrcSet,
      productName,
      onScroll,
      singleColumnExperimentName,
      onProductPosition,
      onIntersectObserved,
      profileClick,
      displayName,
      EXPERIMENTS_PRODUCTS_ABOVE_FOLD_ON_POST_DETAILS,
    };
  },
  async asyncData({ params, error, store, redirect }: Context) {
    const { id } = params;
    const displayName = params.displayName;

    try {
      const ltksStore = getLtksStore(store);
      await ltksStore.fetchLtkById({ id, fetchNestedModels: true });
      const ltk = ltksStore.ltks[id];
      if (ltk === undefined) {
        error({ statusCode: NOT_FOUND, message: `Ltk not found (${id})` });
        return params;
      }

      // Update display name returned from the service as the actual displayName.
      // Solves an issue where a mistyped display name can still be resolved by the post id
      // Leaves data integrity to the API data and not user input
      const profilesStore = getProfilesStore(store);
      const profileId = ltk.profileId;
      const profile = profilesStore.profiles[profileId];
      // If a profile is missing from the sideloads, it means the profile has been deleted
      if (profile === undefined) {
        error({ statusCode: NOT_FOUND, message: `Profile not found (${profileId})` });
        return params;
      }

      if (profile.displayName !== params.displayName) {
        redirect(FOUND, `/explore/${profile.displayName}/posts/${id}`);
        return params;
      }

      getExperimentsStore(store).fetchVariant({ variantName: EXPERIMENTS_POSTS_FAVORITE_VPM });
      getExperimentsStore(store).fetchVariant({ variantName: EXPERIMENTS_PRODUCTS_ABOVE_FOLD_ON_POST_DETAILS });
    } catch (err) {
      logger(logLevel.ERROR, "Error fetching ltk detail", {}, err);
      error({
        statusCode: handleErrorStatusCode(err),
        message: `Error fetching ltk detail (${params.id}) - ${formatErrorMessage(err)}`,
      });
    }

    return { ...params, displayName };
  },
  head: {},
});
